/**
 * Parse and validate website embed origin allowlists (HTTPS only, no wildcards).
 */

export interface EmbedOriginsValidationResult {
  origins: string[];
  errors: string[];
}

const ORIGIN_PATTERN = /^https:\/\/[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*(?::\d{1,5})?$/;

function normalizeOriginInput(raw: string): string {
  return raw.trim().replace(/\/+$/, '');
}

/**
 * Validates a list of origin strings for embed allowlist storage.
 */
export function validateEmbedOrigins(inputs: string[]): EmbedOriginsValidationResult {
  const errors: string[] = [];
  const origins: string[] = [];
  const seen = new Set<string>();

  for (const raw of inputs) {
    const trimmed = raw.trim();
    if (!trimmed) continue;

    if (trimmed.includes('*')) {
      errors.push(`Wildcards are not allowed: ${trimmed}`);
      continue;
    }

    if (!trimmed.startsWith('https://')) {
      errors.push(`Only HTTPS origins are allowed: ${trimmed}`);
      continue;
    }

    const normalized = normalizeOriginInput(trimmed);
    if (!ORIGIN_PATTERN.test(normalized)) {
      errors.push(`Invalid origin format: ${trimmed}`);
      continue;
    }

    if (!seen.has(normalized)) {
      seen.add(normalized);
      origins.push(normalized);
    }
  }

  return { origins, errors };
}

/**
 * Parses newline or comma-separated origin text from settings UI.
 */
export function parseEmbedOriginsText(text: string): string[] {
  return text
    .split(/[\n,]+/)
    .map((line) => line.trim())
    .filter(Boolean);
}

/**
 * Builds CSP frame-ancestors directive value from allowed origins.
 */
export function buildFrameAncestorsDirective(origins: string[]): string {
  const unique = [...new Set(origins.map(normalizeOriginInput))];
  if (unique.length === 0) {
    return "frame-ancestors 'none'";
  }
  return `frame-ancestors 'self' ${unique.join(' ')}`;
}
